import { useEffect, useState } from 'react'

import logoUrl from '@/assets/vaagon-logo.svg'
import { cn } from '@/lib/cn'
import { goldGradientText } from '@/lib/brandGradient'

const SPLASH_TOTAL_MS = 3400
const SWEEP_MS = 1350

type BrandLoadingPageProps = {
  onComplete: () => void
  /** When false, splash holds on the last frame until app init finishes. */
  readyToFinish?: boolean
}

export function BrandLoadingPage({
  onComplete,
  readyToFinish = true,
}: BrandLoadingPageProps) {
  const [phase, setPhase] = useState<'idle' | 'sweep' | 'subtitle' | 'exit'>(
    'idle',
  )
  const [animFinished, setAnimFinished] = useState(false)

  useEffect(() => {
    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches
    if (reducedMotion) {
      const t = window.setTimeout(() => setAnimFinished(true), 400)
      return () => window.clearTimeout(t)
    }

    const sweepStart = window.setTimeout(() => setPhase('sweep'), 120)
    const subtitleAt = window.setTimeout(
      () => setPhase('subtitle'),
      120 + SWEEP_MS + 80,
    )
    const exitAt = window.setTimeout(
      () => setPhase('exit'),
      SPLASH_TOTAL_MS - 280,
    )
    const doneAt = window.setTimeout(
      () => setAnimFinished(true),
      SPLASH_TOTAL_MS,
    )

    return () => {
      window.clearTimeout(sweepStart)
      window.clearTimeout(subtitleAt)
      window.clearTimeout(exitAt)
      window.clearTimeout(doneAt)
    }
  }, [])

  useEffect(() => {
    if (animFinished && readyToFinish) onComplete()
  }, [animFinished, readyToFinish, onComplete])

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Loading Vaagon"
      className={cn(
        'brand-splash fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black',
        phase === 'exit' && 'brand-splash--exit',
      )}
    >
      <div className="flex flex-col items-center gap-8 px-6">
        <div
          className={cn(
            'brand-splash-logo relative w-[min(72vw,220px)]',
            phase === 'sweep' && 'brand-splash-logo--sweep',
            phase === 'subtitle' && 'brand-splash-logo--done',
            phase === 'exit' && 'brand-splash-logo--done',
          )}
        >
          <img
            src={logoUrl}
            alt=""
            aria-hidden
            className="brand-splash-logo-color block h-auto w-full select-none"
            draggable={false}
          />

          <img
            src={logoUrl}
            alt="Vaagon"
            className="brand-splash-logo-mask absolute inset-0 block h-auto w-full brightness-0 select-none"
            draggable={false}
          />
        </div>

        <p
          className={cn(
            'brand-splash-tagline flex items-center gap-3 text-center text-xs font-semibold tracking-[0.28em] uppercase sm:gap-4 sm:text-sm md:text-base',
            (phase === 'subtitle' || phase === 'exit') &&
              'brand-splash-tagline--visible',
          )}
        >
          <span
            className="text-[11px] tracking-normal text-[#E0A263]/55"
            aria-hidden
          >
            --
          </span>
          <span className={goldGradientText}>Startups In Motion</span>
          <span
            className="text-[11px] tracking-normal text-[#E0A263]/55"
            aria-hidden
          >
            --
          </span>
        </p>
      </div>
    </div>
  )
}
